import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { useAuth } from "@/contexts/AuthContext";

const AppCtx = createContext(null);

export function AppProvider({ children }) {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [profile, setProfile] = useState(null);
  const [activeJobId, setActiveJobId] = useState(() => localStorage.getItem("activeJobId") || "");

  const reloadJobs = useCallback(async () => {
    if (!user) return;
    try {
      const [{ data: j }, { data: c }] = await Promise.all([
        api.get("/jobs"),
        api.get("/categories"),
      ]);
      setJobs(j);
      setCategories(c);
      // Sanitise activeJobId against the freshly-loaded jobs list. If the
      // stored id no longer belongs to the current user (deleted job,
      // account switch on the same browser, etc.) clear it so downstream
      // widgets — JobHomeWidget, CostBreakdownStrip, Capture header, etc.
      // — snap back to a clean "no active job" state instead of firing
      // per-job requests against an id the user doesn't own.
      const exists = activeJobId && j.some((job) => job.id === activeJobId);
      if (activeJobId && !exists) {
        setActiveJobId("");
        localStorage.removeItem("activeJobId");
      } else if (!activeJobId && j.length > 0) {
        setActiveJobId(j[0].id);
        localStorage.setItem("activeJobId", j[0].id);
      }
    } catch (e) {
      devLog.warn("reloadJobs failed", e);
    }
  }, [user, activeJobId]);

  const reloadProfile = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/profile");
      setProfile(data);
    } catch (e) {
      devLog.warn("reloadProfile failed", e);
    }
  }, [user]);

  useEffect(() => { reloadJobs(); }, [reloadJobs]);
  useEffect(() => { reloadProfile(); }, [reloadProfile]);

  const setActive = useCallback((id) => {
    setActiveJobId(id);
    if (id) localStorage.setItem("activeJobId", id);
  }, []);

  const value = useMemo(
    () => ({ jobs, categories, profile, activeJobId, setActiveJobId: setActive, reloadJobs, reloadProfile }),
    [jobs, categories, profile, activeJobId, setActive, reloadJobs, reloadProfile]
  );

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

export function useApp() {
  return useContext(AppCtx);
}
